import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { Receipt, Plus, X, MagnifyingGlass, ArrowSquareOut, Trash, Building, Download } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CustomChargeModal } from "@/components/CustomChargeModal";

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "paid", label: "Paid" },
  { id: "cancelled", label: "Cancelled" },
];

export default function AdminCharges() {
  const [charges, setCharges] = useState([]);
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [chargeTarget, setChargeTarget] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [c, cl] = await Promise.all([
        api.get("/admin/custom-charges"),
        api.get("/admin/clients"),
      ]);
      setCharges(c.data || []);
      setClients(cl.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const cancel = async (id) => {
    if (!window.confirm("Cancel this pending charge? The client will no longer be able to pay it.")) return;
    try {
      await api.delete(`/admin/custom-charges/${id}`);
      toast.success("Charge cancelled");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not cancel");
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return charges
      .filter((c) => filter === "all" || c.status === filter)
      .filter((c) => {
        if (!term) return true;
        return (
          c.client_name?.toLowerCase().includes(term) ||
          c.client_email?.toLowerCase().includes(term) ||
          c.description?.toLowerCase().includes(term)
        );
      });
  }, [charges, filter, search]);

  const totals = useMemo(() => {
    const sum = (arr) => arr.reduce((s, c) => s + (c.amount || 0), 0);
    return {
      pending: sum(charges.filter((c) => c.status === "pending")),
      paid: sum(charges.filter((c) => c.status === "paid")),
      cancelled: sum(charges.filter((c) => c.status === "cancelled")),
      pendingCount: charges.filter((c) => c.status === "pending").length,
      paidCount: charges.filter((c) => c.status === "paid").length,
    };
  }, [charges]);

  const filteredClients = useMemo(() => {
    const term = pickerSearch.trim().toLowerCase();
    if (!term) return clients.slice(0, 30);
    return clients.filter(
      (c) => c.name?.toLowerCase().includes(term) || c.email?.toLowerCase().includes(term)
    );
  }, [clients, pickerSearch]);

  return (
    <AdminLayout>
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Custom charges</div>
          <h1 className="font-display text-5xl mt-2">Billing extras</h1>
          <p className="text-[#a3a3a3] mt-2 text-sm max-w-xl">One-off payment requests for over-budget pages, plugin licenses, late fees, anything outside the standard catalog.</p>
        </div>
        <button
          onClick={() => { setPickerOpen(true); setPickerSearch(""); }}
          data-testid="new-charge-btn"
          className="btn-primary px-5 py-3 text-sm font-bold uppercase tracking-wider flex items-center gap-2"
        >
          <Plus size={14} weight="bold" /> New charge
        </button>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#262626] border border-[#262626] mb-6">
        <div className="bg-[#0a0a0a] p-5">
          <div className="text-[10px] text-[#a3a3a3] uppercase tracking-wider">Pending</div>
          <div className="font-display text-3xl mt-1 text-[#FF2D2D] tabular-nums" data-testid="totals-pending">{formatUSD(totals.pending)}</div>
          <div className="text-xs text-[#a3a3a3] mt-1">{totals.pendingCount} request{totals.pendingCount === 1 ? "" : "s"}</div>
        </div>
        <div className="bg-[#0a0a0a] p-5">
          <div className="text-[10px] text-[#a3a3a3] uppercase tracking-wider">Collected</div>
          <div className="font-display text-3xl mt-1 text-[#22c55e] tabular-nums" data-testid="totals-paid">{formatUSD(totals.paid)}</div>
          <div className="text-xs text-[#a3a3a3] mt-1">{totals.paidCount} paid</div>
        </div>
        <div className="bg-[#0a0a0a] p-5">
          <div className="text-[10px] text-[#a3a3a3] uppercase tracking-wider">Cancelled</div>
          <div className="font-display text-3xl mt-1 text-[#a3a3a3] tabular-nums" data-testid="totals-cancelled">{formatUSD(totals.cancelled)}</div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex gap-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                filter === f.id
                  ? "border-[#FF2D2D] bg-[#FF2D2D] text-white"
                  : "border-[#262626] text-[#a3a3a3] hover:border-[#FF2D2D] hover:text-[#FF2D2D]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a3a3a3]" />
          <input
            type="text"
            placeholder="Search by client, email, or description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="charge-search"
            className="w-full bg-[#050505] border border-[#262626] pl-9 pr-3 py-2 text-sm focus:border-[#FF2D2D] outline-none"
          />
        </div>
      </div>

      {/* List */}
      <div className="border border-[#262626] divide-y divide-[#262626]" data-testid="charges-list">
        <div className="hidden md:grid grid-cols-12 gap-4 p-4 bg-[#0f0f0f] text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3]">
          <div className="col-span-3">Client</div>
          <div className="col-span-4">Description</div>
          <div className="col-span-2">Amount</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">—</div>
        </div>
        {loading ? (
          <div className="bg-[#0a0a0a] p-12 text-center text-[#a3a3a3]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-[#0a0a0a] p-12 text-center text-[#a3a3a3]">
            {charges.length === 0 ? "No custom charges yet. Click 'New charge' above to issue one." : "No charges match your filters."}
          </div>
        ) : (
          filtered.map((c) => (
            <div
              key={c.id}
              className="grid grid-cols-12 gap-4 p-4 items-center bg-[#0a0a0a]"
              data-testid={`charge-row-${c.id}`}
            >
              <div className="col-span-12 md:col-span-3 min-w-0">
                <div className="font-semibold truncate">{c.client_name}</div>
                <div className="text-xs text-[#a3a3a3] truncate">{c.client_email}</div>
              </div>
              <div className="col-span-12 md:col-span-4 min-w-0">
                <div className="text-sm truncate" title={c.description}>{c.description}</div>
                <div className="text-xs text-[#666] mt-0.5 flex items-center gap-2">
                  <span>{new Date(c.created_at).toLocaleDateString()}</span>
                  {c.project_id && (
                    <Link to={`/admin/projects/${c.project_id}`} className="text-[#FF2D2D] hover:underline flex items-center gap-1">
                      <Building size={10} /> Project
                    </Link>
                  )}
                </div>
              </div>
              <div className="col-span-6 md:col-span-2">
                <div className="font-display text-xl tabular-nums">{formatUSD(c.amount)}</div>
              </div>
              <div className="col-span-6 md:col-span-2">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${
                  c.status === "paid" ? "border-[#22c55e]/40 text-[#22c55e]" :
                  c.status === "cancelled" ? "border-[#404040] text-[#a3a3a3]" :
                  "border-[#FF2D2D] text-[#FF2D2D]"
                }`}>{c.status}</span>
                {c.status === "paid" && c.paid_at && (
                  <div className="text-xs text-[#666] mt-1">{new Date(c.paid_at).toLocaleDateString()}</div>
                )}
              </div>
              <div className="col-span-12 md:col-span-1 flex justify-end gap-1.5">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/charges/${c.id}/invoice.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`charge-invoice-${c.id}`}
                  title="Download invoice PDF"
                  className="px-2 py-1 text-xs border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D]"
                >
                  <Download size={12} />
                </a>
                <Link
                  to={`/charges/${c.id}`}
                  data-testid={`open-charge-${c.id}`}
                  title="Open client view"
                  className="px-2 py-1 text-xs border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D]"
                >
                  <ArrowSquareOut size={12} />
                </Link>
                {c.status === "pending" && (
                  <button
                    onClick={() => cancel(c.id)}
                    data-testid={`cancel-charge-${c.id}`}
                    title="Cancel charge"
                    className="px-2 py-1 text-xs border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D]"
                  >
                    <Trash size={12} />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Client picker modal */}
      {pickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="charge-picker">
          <div className="border border-[#262626] bg-[#0a0a0a] w-full max-w-lg p-6 max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Pick a client</div>
                <h3 className="font-display text-2xl mt-1">Who's this charge for?</h3>
              </div>
              <button onClick={() => setPickerOpen(false)} className="text-[#a3a3a3] hover:text-white"><X size={18} /></button>
            </div>
            <div className="relative mb-4">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a3a3a3]" />
              <input
                type="text"
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                autoFocus
                placeholder="Search by name or email"
                data-testid="picker-search"
                className="w-full bg-[#050505] border border-[#262626] pl-9 pr-3 py-2.5 text-sm focus:border-[#FF2D2D] outline-none"
              />
            </div>
            <div className="overflow-y-auto flex-1 -mx-2 divide-y divide-[#262626]">
              {filteredClients.length === 0 ? (
                <div className="p-6 text-center text-[#a3a3a3] text-sm">No clients match.</div>
              ) : (
                filteredClients.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => { setChargeTarget(c); setPickerOpen(false); }}
                    data-testid={`pick-client-${c.id}`}
                    className="w-full text-left px-3 py-2.5 hover:bg-[#121212] flex items-center justify-between"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{c.name}</div>
                      <div className="text-xs text-[#a3a3a3] truncate">{c.email} {c.company_name && <span className="text-[#666]">· {c.company_name}</span>}</div>
                    </div>
                    {c.has_maintenance_plan && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#FF2D2D] text-[#FF2D2D] shrink-0">Plan</span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <CustomChargeModal
        open={!!chargeTarget}
        client={chargeTarget}
        onClose={() => setChargeTarget(null)}
        onCreated={() => load()}
      />
    </AdminLayout>
  );
}
